import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { Dropdown, Menu } from 'antd';
import { TbDatabase } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useMaps } from '@/hooks/useMaps';
import {
    BaseSkeleton,
    DropdownMenuLabel,
    DropdownOptionContent,
    DropdownPanel,
    DropdownPanelMenu,
    EmptySection,
    LearnMoreFooter,
} from '@/shared';
import { useConnectedAccountStore, useReportComponentStore } from '@/stores';
import { ConnectedAccountType, ReportComponent, ReportComponentType } from '@/types';
import { DataSourceType, ReportComponentConfig } from '@/types/api/modules/business-intelligence';

import { DatasetSchemaField, useDatasetSchema } from '../../hooks';

interface DatasetDefinitionSelectionDropdownProps {
    component: ReportComponent;
    children?: ReactNode;
}

const incomeStatementFunnelMetrics = [
    { field: 'revenue', label: 'Revenue', aggregation: 'sum' as const },
    { field: 'costOfRevenue', label: 'Cost Of Revenue', aggregation: 'sum' as const },
    { field: 'grossProfit', label: 'Gross Profit', aggregation: 'sum' as const },
    { field: 'researchAndDevelopmentExpenses', label: 'Research & Development Expenses', aggregation: 'sum' as const },
    {
        field: 'sellingGeneralAndAdministrativeExpenses',
        label: 'Selling, General & Administrative Expenses',
        aggregation: 'sum' as const,
    },
    { field: 'operatingExpenses', label: 'Operating Expenses', aggregation: 'sum' as const },
    { field: 'operatingIncome', label: 'Operating Income', aggregation: 'sum' as const },
    { field: 'incomeTaxExpense', label: 'Income Tax Expense', aggregation: 'sum' as const },
    { field: 'netIncome', label: 'Net Income', aggregation: 'sum' as const },
];

const getDefaultDimensionsForComponent = (componentType: ReportComponentType, dataSetType?: string) => {
    if (dataSetType !== DataSourceType.PLATFORM_FINANCIAL) {
        return [];
    }
    if (
        [ReportComponentType.FUNNEL_CHART, ReportComponentType.GAUGE_CHART, ReportComponentType.TABLE].includes(
            componentType,
        )
    ) {
        return [];
    }
    if ([ReportComponentType.LINE_CHART, ReportComponentType.AREA_CHART].includes(componentType)) {
        return [{ field: 'date', label: 'Date' }];
    }

    return [{ field: 'company', label: 'Company' }];
};

const isConnectedAccountDatasetId = (key?: string) => String(key ?? '').startsWith('connected-account:');
const isConnectedAccountResourcesDatasetId = (key?: string) => String(key ?? '').endsWith(':resources');

const workspaceDefaultDimensionsByDataset = {
    folders: { field: 'name', label: 'Folder' },
    investments: { field: 'name', label: 'Investment' },
    memos: { field: 'title', label: 'Memo' },
    pipelines: { field: 'stage', label: 'Stage' },
    dashboards: { field: 'name', label: 'Dashboard' },
    lists: { field: 'name', label: 'List' },
    tasks: { field: 'title', label: 'Task' },
    teams: { field: 'name', label: 'Team' },
    users: { field: 'user', label: 'Member' },
} as const;

const getDefaultMetricsForDataset = (
    componentType: ReportComponentType,
    datasetKey: string,
    dataSetType?: string,
    dataset?: DatasetSchemaField,
) => {
    if (componentType === ReportComponentType.FUNNEL_CHART && datasetKey === 'incomeStatement') {
        return incomeStatementFunnelMetrics;
    }

    if (dataSetType === DataSourceType.WORKSPACE_ANALYTICS) {
        if (componentType === ReportComponentType.FUNNEL_CHART && datasetKey === 'pipelines') {
            return [{ field: 'opportunityCount', label: 'Opportunities', aggregation: 'sum' as const }];
        }

        return [{ field: 'count', label: 'Count', aggregation: 'sum' as const }];
    }

    if (dataSetType === DataSourceType.THIRD_PARTY && isConnectedAccountDatasetId(datasetKey)) {
        return [
            {
                field: '__row_count',
                aggregation: 'sum' as const,
                label: 'Row Count',
            },
        ];
    }

    if (dataset?.defaultMetric) {
        return [dataset.defaultMetric];
    }

    return [];
};

const getDefaultDimensionsForDataset = (
    componentType: ReportComponentType,
    datasetKey: string,
    dataSetType?: string,
    dataset?: DatasetSchemaField,
) => {
    if (dataSetType === DataSourceType.WORKSPACE_ANALYTICS) {
        if ([ReportComponentType.GAUGE_CHART, ReportComponentType.TABLE].includes(componentType)) {
            return [];
        }

        const defaultDimension =
            workspaceDefaultDimensionsByDataset[datasetKey as keyof typeof workspaceDefaultDimensionsByDataset];

        return defaultDimension ? [defaultDimension] : [];
    }

    if (dataSetType === DataSourceType.THIRD_PARTY && isConnectedAccountResourcesDatasetId(datasetKey)) {
        return [{ field: 'resourceType', label: 'Resource Type' }];
    }

    if (dataset?.defaultDimension && componentType !== ReportComponentType.GAUGE_CHART) {
        return [dataset.defaultDimension];
    }

    return getDefaultDimensionsForComponent(componentType, dataSetType);
};

const DatasetDefinitionSelectionDropdown: FC<DatasetDefinitionSelectionDropdownProps> = ({
    component,
    children,
}): JSX.Element => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [hasRequestedConnectedAccounts, setHasRequestedConnectedAccounts] = useState(false);
    const [search, setSearch] = useState('');

    const { token } = useAntDesignTheme();
    const { integrationLabelToIconMap, reportComponentConfigurationDatasetCategoryIconMap } = useMaps();

    const [width, setWidth] = useState<number>();
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!ref.current) {
            return;
        }

        const observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                setWidth(entry.contentRect.width);
            }
        });

        observer.observe(ref.current);

        return () => observer.disconnect();
    }, []);

    const replaceDraft = useReportComponentStore((s) => s.replaceDraftConfiguration);
    const draftConfigurations = useReportComponentStore((s) => s.draftConfigurations);

    const draft = draftConfigurations[component?.id as string];
    const { connectedAccounts, fetchConnectedAccounts } = useConnectedAccountStore();
    const selectedConnectedAccount = connectedAccounts.find((account) => account.id === draft?.dataSet?.providerId);
    const selectedConnectedAccountIconSrc = selectedConnectedAccount?.type
        ? integrationLabelToIconMap[selectedConnectedAccount.type as ConnectedAccountType]
        : undefined;

    const { schema, isLoading } = useDatasetSchema(draft as ReportComponentConfig, { includeCategory: false });

    const options = schema?.datasets ?? [];
    const normalizedSearch = search.trim().toLocaleLowerCase();
    const filteredOptions = normalizedSearch
        ? options.filter((option: { description?: string; key?: string; label?: string }) =>
              [option.label, option.key, option.description].some((field) =>
                  field?.toLocaleLowerCase().includes(normalizedSearch),
              ),
          )
        : options;
    const isThirdPartyDataset = draft?.dataSet?.type === DataSourceType.THIRD_PARTY;

    const handleDropdownOpenChange = (nextOpen: boolean) => {
        setDropdownOpen(nextOpen);

        if (!nextOpen) {
            setSearch('');
        }

        if (nextOpen && isThirdPartyDataset && !hasRequestedConnectedAccounts && connectedAccounts.length === 0) {
            setHasRequestedConnectedAccounts(true);
            void fetchConnectedAccounts();
        }
    };

    return (
        <Dropdown
            open={dropdownOpen}
            onOpenChange={handleDropdownOpenChange}
            trigger={['click']}
            overlayStyle={{ width }}
            popupRender={() => (
                <DropdownPanel
                    footer={<LearnMoreFooter topic='our data' variant='dropdown' />}
                    search={{
                        placeholder: 'Filter Categories...',
                        setValue: setSearch,
                        value: search,
                    }}
                    style={{ marginTop: '0.25rem' }}
                    width={width}
                >
                    <DropdownMenuLabel>
                        {filteredOptions.length} Option{filteredOptions.length === 1 ? '' : 's'}
                    </DropdownMenuLabel>
                    <DropdownPanelMenu selectable={false}>
                        {isLoading ? (
                            <Menu.Item disabled key='loading-options'>
                                <BaseSkeleton paragraph={{ rows: 3 }} />
                            </Menu.Item>
                        ) : filteredOptions.length !== 0 ? (
                            filteredOptions.map((o: DatasetSchemaField) => {
                                return (
                                    <Menu.Item
                                        key={o?.key}
                                        onClick={() => {
                                            if (draft?.dataSet?.key === o.key) {
                                                return;
                                            }

                                            const baseDraft = (draft ??
                                                component.configuration ??
                                                {}) as Partial<ReportComponentConfig>;

                                            replaceDraft(component.id, {
                                                ...baseDraft,
                                                data: undefined,
                                                dataSet: {
                                                    type: draft?.dataSet?.type ?? DataSourceType.PLATFORM_FINANCIAL,
                                                    ...draft?.dataSet,
                                                    key: o.key,
                                                },
                                                query: {
                                                    datasetId: o.key as string,
                                                    metrics: getDefaultMetricsForDataset(
                                                        component.type,
                                                        o.key,
                                                        draft?.dataSet?.type,
                                                        o,
                                                    ),
                                                    dimensions: getDefaultDimensionsForDataset(
                                                        component.type,
                                                        o.key,
                                                        draft?.dataSet?.type,
                                                        o,
                                                    ),
                                                },
                                            });
                                        }}
                                    >
                                        <DropdownOptionContent
                                            avatar={{
                                                src: isThirdPartyDataset ? selectedConnectedAccountIconSrc : undefined,
                                                icon:
                                                    isThirdPartyDataset && selectedConnectedAccountIconSrc
                                                        ? undefined
                                                        : (reportComponentConfigurationDatasetCategoryIconMap[
                                                              (o?.icon ?? o?.key) as string
                                                          ] ?? (
                                                              <TbDatabase
                                                                  style={{ color: token.colorTextTertiary }}
                                                                  size='75%'
                                                              />
                                                          )),
                                            }}
                                            title={o?.label}
                                            metadata={[
                                                `${o?.metricsCount ?? 0} Metrics`,
                                                `${o?.dimensionsCount ?? 0} Dimensions`,
                                            ]}
                                            selected={draft?.dataSet?.key === o?.key}
                                        />
                                    </Menu.Item>
                                );
                            })
                        ) : (
                            <EmptySection
                                height='12.5rem'
                                icon={<TbDatabase size='75%' style={{ color: token.colorTextTertiary }} />}
                                label='No Category Options'
                                description={
                                    search
                                        ? 'No dataset categories match your search.'
                                        : "You don't have any dataset categories to select."
                                }
                            />
                        )}
                    </DropdownPanelMenu>
                </DropdownPanel>
            )}
        >
            <div ref={ref}>
                <span>{children}</span>
            </div>
        </Dropdown>
    );
};

export default DatasetDefinitionSelectionDropdown;
